import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  getPatientPunchCards,
  getActivePunchCard,
  createPunchCard,
  updatePunchCard,
  closePunchCard,
  deletePunchCard,
  getPunchedAppointments,
  createManualIncomeEntry,
  logActivity,
  ACTIVITY_ACTIONS,
  ENTITY_TYPES,
} from "../../firebase";

// Cross-API invalidation tags for financeApi when a punch card purchase
// records income (the upfront payment appears in stats/history/kanban)
const CROSS_INVALIDATE_FINANCE = [
  { type: "TransactionStats", id: "MONTHLY" },
  { type: "Transactions", id: "PAID_HISTORY" },
  { type: "KanbanTransactions", id: "LIST" },
];

export const punchCardsApi = createApi({
  reducerPath: "punchCardsApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["PunchCards", "PunchedAppointments"],
  endpoints: (builder) => ({
    // Get all punch cards for a patient (active + history)
    getPatientPunchCards: builder.query({
      async queryFn({ clinicId, patientId }) {
        try {
          const cards = await getPatientPunchCards(clinicId, patientId);
          return { data: cards };
        } catch (error) {
          console.error("[CK-7700] Error fetching patient punch cards:", {
            errorCode: "CK-7700",
            errorMessage: error.message,
            clinicId,
            patientId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { patientId }) => [
        { type: "PunchCards", id: `PATIENT-${patientId}` },
      ],
    }),

    // Get the patient's single active punch card (or null)
    getActivePunchCard: builder.query({
      async queryFn({ clinicId, patientId }) {
        try {
          const card = await getActivePunchCard(clinicId, patientId);
          return { data: card };
        } catch (error) {
          console.error("[CK-7701] Error fetching active punch card:", {
            errorCode: "CK-7701",
            errorMessage: error.message,
            clinicId,
            patientId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { patientId }) => [
        { type: "PunchCards", id: `PATIENT-${patientId}` },
        { type: "PunchCards", id: `ACTIVE-${patientId}` },
      ],
    }),

    // Appointments punched against a card (punch history)
    getPunchedAppointments: builder.query({
      async queryFn({ clinicId, cardId }) {
        try {
          const appointments = await getPunchedAppointments(clinicId, cardId);
          return { data: appointments };
        } catch (error) {
          console.error("[CK-7702] Error fetching punched appointments:", {
            errorCode: "CK-7702",
            errorMessage: error.message,
            clinicId,
            cardId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { cardId }) => [
        { type: "PunchedAppointments", id: cardId },
      ],
    }),

    // Create a punch card; optionally records the upfront purchase as income
    createPunchCard: builder.mutation({
      async queryFn(
        {
          clinicId,
          patientId,
          cardData,
          recordPurchaseIncome,
          therapistId,
          therapistName,
        },
        { dispatch }
      ) {
        try {
          const card = await createPunchCard(
            clinicId,
            patientId,
            cardData,
            therapistId,
            therapistName
          );

          // Optionally record the full upfront amount as paid income.
          // Once recorded, punched meetings are created at ₪0 so revenue is
          // never counted twice.
          if (recordPurchaseIncome && cardData.paidUpfront === true) {
            const totalAmount =
              (Number(cardData.pricePerVisit) || 0) *
              (Number(cardData.totalVisits) || 0);

            const purchaseTransaction = await createManualIncomeEntry(
              clinicId,
              therapistId,
              {
                patientId,
                patientName: cardData.patientName || null,
                amount: totalAmount,
                paymentMethod: cardData.purchasePaymentMethod || null,
                paymentDate: cardData.purchaseDate || null,
                description: `רכישת כרטיסיה — ${cardData.totalVisits} מפגשים`,
                isPunchCardPurchase: true,
                punchCardId: card.id,
              }
            );

            await updatePunchCard(clinicId, patientId, card.id, {
              purchaseIncomeRecorded: true,
              purchaseTransactionId: purchaseTransaction.id,
            });

            card.purchaseIncomeRecorded = true;
            card.purchaseTransactionId = purchaseTransaction.id;

            dispatch({
              type: "financeApi/invalidateTags",
              payload: CROSS_INVALIDATE_FINANCE,
            });
          }

          logActivity(clinicId, {
            therapistId,
            therapistName,
            action: ACTIVITY_ACTIONS.CREATE_PUNCH_CARD,
            entityType: ENTITY_TYPES.PUNCH_CARD,
            entityId: card.id,
            entityName: cardData.patientName || null,
            details: {
              totalVisits: card.totalVisits,
              pricePerVisit: card.pricePerVisit,
              paidUpfront: card.paidUpfront,
              purchaseIncomeRecorded: card.purchaseIncomeRecorded,
            },
          });

          return { data: card };
        } catch (error) {
          console.error("[CK-7703] Error creating punch card:", {
            errorCode: "CK-7703",
            errorMessage: error.message,
            clinicId,
            patientId,
            timestamp: new Date().toISOString(),
          });
          logActivity(clinicId, {
            therapistId,
            therapistName,
            action: ACTIVITY_ACTIONS.CREATE_PUNCH_CARD,
            entityType: ENTITY_TYPES.PUNCH_CARD,
            entityName: cardData?.patientName || null,
            success: false,
            errorMessage: error.message,
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { patientId }) => [
        { type: "PunchCards", id: `PATIENT-${patientId}` },
        { type: "PunchCards", id: `ACTIVE-${patientId}` },
      ],
    }),

    // Update a punch card (add visits, expiry, notes...)
    updatePunchCard: builder.mutation({
      async queryFn({
        clinicId,
        patientId,
        cardId,
        updateData,
        therapistId,
        therapistName,
        patientName,
      }) {
        try {
          const card = await updatePunchCard(
            clinicId,
            patientId,
            cardId,
            updateData
          );

          logActivity(clinicId, {
            therapistId,
            therapistName,
            action: ACTIVITY_ACTIONS.UPDATE_PUNCH_CARD,
            entityType: ENTITY_TYPES.PUNCH_CARD,
            entityId: cardId,
            entityName: patientName || null,
            details: { updatedFields: Object.keys(updateData || {}) },
          });

          return { data: card };
        } catch (error) {
          console.error("[CK-7704] Error updating punch card:", {
            errorCode: "CK-7704",
            errorMessage: error.message,
            clinicId,
            patientId,
            cardId,
            timestamp: new Date().toISOString(),
          });
          logActivity(clinicId, {
            therapistId,
            therapistName,
            action: ACTIVITY_ACTIONS.UPDATE_PUNCH_CARD,
            entityType: ENTITY_TYPES.PUNCH_CARD,
            entityId: cardId,
            entityName: patientName || null,
            success: false,
            errorMessage: error.message,
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { patientId, cardId }) => [
        { type: "PunchCards", id: `PATIENT-${patientId}` },
        { type: "PunchCards", id: `ACTIVE-${patientId}` },
        { type: "PunchedAppointments", id: cardId },
      ],
    }),

    // Close a punch card (soft close - remaining visits forfeited)
    closePunchCard: builder.mutation({
      async queryFn({
        clinicId,
        patientId,
        cardId,
        closeReason,
        therapistId,
        therapistName,
        patientName,
      }) {
        try {
          const card = await closePunchCard(clinicId, patientId, cardId, {
            closedBy: therapistId,
            closeReason,
          });

          logActivity(clinicId, {
            therapistId,
            therapistName,
            action: ACTIVITY_ACTIONS.CLOSE_PUNCH_CARD,
            entityType: ENTITY_TYPES.PUNCH_CARD,
            entityId: cardId,
            entityName: patientName || null,
            details: { closeReason: closeReason || null },
          });

          return { data: card };
        } catch (error) {
          console.error("[CK-7705] Error closing punch card:", {
            errorCode: "CK-7705",
            errorMessage: error.message,
            clinicId,
            patientId,
            cardId,
            timestamp: new Date().toISOString(),
          });
          logActivity(clinicId, {
            therapistId,
            therapistName,
            action: ACTIVITY_ACTIONS.CLOSE_PUNCH_CARD,
            entityType: ENTITY_TYPES.PUNCH_CARD,
            entityId: cardId,
            entityName: patientName || null,
            success: false,
            errorMessage: error.message,
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { patientId, cardId }) => [
        { type: "PunchCards", id: `PATIENT-${patientId}` },
        { type: "PunchCards", id: `ACTIVE-${patientId}` },
        { type: "PunchedAppointments", id: cardId },
      ],
    }),

    // Hard-delete a punch card (only when no punches were made)
    deletePunchCard: builder.mutation({
      async queryFn(
        { clinicId, patientId, cardId, therapistId, therapistName, patientName },
        { dispatch }
      ) {
        try {
          await deletePunchCard(clinicId, patientId, cardId);
          // The purchase income row was cancelled inside deletePunchCard —
          // refresh finance views.
          dispatch({
            type: "financeApi/invalidateTags",
            payload: CROSS_INVALIDATE_FINANCE,
          });

          logActivity(clinicId, {
            therapistId,
            therapistName,
            action: ACTIVITY_ACTIONS.DELETE_PUNCH_CARD,
            entityType: ENTITY_TYPES.PUNCH_CARD,
            entityId: cardId,
            entityName: patientName || null,
          });

          return { data: cardId };
        } catch (error) {
          console.error("[CK-7706] Error deleting punch card:", {
            errorCode: "CK-7706",
            errorMessage: error.message,
            clinicId,
            patientId,
            cardId,
            timestamp: new Date().toISOString(),
          });
          logActivity(clinicId, {
            therapistId,
            therapistName,
            action: ACTIVITY_ACTIONS.DELETE_PUNCH_CARD,
            entityType: ENTITY_TYPES.PUNCH_CARD,
            entityId: cardId,
            entityName: patientName || null,
            success: false,
            errorMessage: error.message,
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { patientId }) => [
        { type: "PunchCards", id: `PATIENT-${patientId}` },
        { type: "PunchCards", id: `ACTIVE-${patientId}` },
      ],
    }),
  }),
});

export const {
  useGetPatientPunchCardsQuery,
  useGetActivePunchCardQuery,
  useGetPunchedAppointmentsQuery,
  useCreatePunchCardMutation,
  useUpdatePunchCardMutation,
  useClosePunchCardMutation,
  useDeletePunchCardMutation,
} = punchCardsApi;
